import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import SmartTimeEntryForm from '../components/SmartTimeEntryForm';
import TimeEntryList from '../components/TimeEntryList';
import { TimeEntry, timeEntriesAPI } from '../services/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createEmployeeNavSections } from '../components/layout/dashboardNavConfigs';
import { PlusIcon, ArrowLeftIcon, ClockIcon } from '../components/icons';

// Icons
const Icons = {
  plus: <PlusIcon className="w-5 h-5" strokeWidth={2} />,
  arrowLeft: <ArrowLeftIcon className="w-5 h-5" strokeWidth={1.5} />,
  clock: <ClockIcon className="w-6 h-6" strokeWidth={1.5} />,
};

// Component for creating a new time entry
const NewTimeEntry: React.FC<{ onSuccess: () => void; isDark: boolean }> = ({
  onSuccess,
  isDark,
}) => {
  const navigate = useNavigate();

  const handleSuccess = (entry: TimeEntry) => {
    onSuccess();
    navigate('/timesheet');
  };

  const handleCancel = () => {
    navigate('/timesheet');
  };

  return (
    <div
      className={`${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white border-slate-200'} border rounded-2xl p-6`}
    >
      <SmartTimeEntryForm onSuccess={handleSuccess} onCancel={handleCancel} />
    </div>
  );
};

// Component for editing an existing time entry
const EditTimeEntry: React.FC<{ onSuccess: () => void; isDark: boolean }> = ({
  onSuccess,
  isDark,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEntry = async () => {
      if (!id) return;
      try {
        const response = await timeEntriesAPI.getById(parseInt(id));
        setEntry(response.data.time_entry || response.data);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load time entry');
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id]);

  const handleSuccess = (updatedEntry: TimeEntry) => {
    onSuccess();
    navigate('/timesheet');
  };

  const handleCancel = () => {
    navigate('/timesheet');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`p-4 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400' : 'bg-rose-50 border border-rose-200 text-rose-600'}`}
      >
        {error}
        <button
          onClick={() => navigate('/timesheet')}
          className={`ml-4 underline ${isDark ? 'text-rose-300 hover:text-rose-200' : 'text-rose-700 hover:text-rose-800'}`}
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className={`p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' : 'bg-amber-50 border border-amber-200 text-amber-600'}`}
      >
        {t('timeEntry.notFound')}
        <button
          onClick={() => navigate('/timesheet')}
          className={`ml-4 underline ${isDark ? 'text-amber-300 hover:text-amber-200' : 'text-amber-700 hover:text-amber-800'}`}
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white border-slate-200'} border rounded-2xl p-6`}
    >
      <SmartTimeEntryForm
        timeEntry={entry}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
};

// Page Header Component
const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  isDark: boolean;
  action?: React.ReactNode;
  backLink?: string;
}> = ({ title, subtitle, isDark, action, backLink }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`border-b ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}
    >
      <div className="px-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-5">
          <div className="flex items-center gap-4">
            {backLink && (
              <Link
                to={backLink}
                className={`p-2 rounded-xl transition-all ${
                  isDark
                    ? 'text-slate-400 hover:text-white hover:bg-slate-900'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                {Icons.arrowLeft}
              </Link>
            )}
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {title}
              </h1>
              {subtitle && (
                <p
                  className={`mt-0.5 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto">{action}</div>
        </div>
      </div>
    </div>
  );
};

// Main time entry list component
const TimeEntryListView: React.FC<{
  refreshTrigger: number;
  isDark: boolean;
}> = ({ refreshTrigger, isDark }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleEditEntry = (entry: TimeEntry) => {
    navigate(`/timesheet/${entry.id}/edit`);
  };

  return (
    <>
      <PageHeader
        title={t('timeTracking.title')}
        subtitle={t('timeTracking.subtitle')}
        isDark={isDark}
        action={
          <Link
            to="/timesheet/new"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.plus}
            <span>{t('timeTracking.newEntry')}</span>
          </Link>
        }
      />

      {/* Content */}
      <div className="p-6">
        <div
          className={`${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white border-slate-200'} border rounded-2xl overflow-hidden`}
        >
          <TimeEntryList
            onEditEntry={handleEditEntry}
            refreshTrigger={refreshTrigger}
          />
        </div>
      </div>
    </>
  );
};

const TimeTracking: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleFormSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const isActive = (href: string) => {
    if (href === '/employee-dashboard') return location.pathname === '/employee-dashboard';
    return location.pathname.startsWith(href);
  };

  const sections = createEmployeeNavSections(t, isActive);

  const timesheetContent = (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Ambient glow effect for dark mode */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[120px]" />
        </div>
      )}

      <Routes>
        <Route
          index
          element={
            <TimeEntryListView
              refreshTrigger={refreshTrigger}
              isDark={isDark}
            />
          }
        />
        <Route
          path="new"
          element={
            <>
              <PageHeader
                title={t('timeTracking.newEntry')}
                subtitle={t('timeTracking.subtitle')}
                isDark={isDark}
                backLink="/timesheet"
              />
              <div className="p-6">
                <NewTimeEntry onSuccess={handleFormSuccess} isDark={isDark} />
              </div>
            </>
          }
        />
        <Route
          path=":id/edit"
          element={
            <>
              <PageHeader
                title={t('timeTracking.editEntry')}
                subtitle={t('timeTracking.subtitle')}
                isDark={isDark}
                backLink="/timesheet"
              />
              <div className="p-6">
                <EditTimeEntry onSuccess={handleFormSuccess} isDark={isDark} />
              </div>
            </>
          }
        />
      </Routes>
    </div>
  );

  return <DashboardLayout sections={sections}>{timesheetContent}</DashboardLayout>;
};

export default TimeTracking;
